import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type CustomHttpParams = Record<string, string | number | boolean | readonly (string | number | boolean)[]>;

export interface HttpConfigOptions {
  context?: HttpContext;
  endpoint?: string;
  headers?: HttpHeaders | Record<string, string | string[]>;
  observe?: 'body' | 'events' | 'response';
  params?: HttpParams | CustomHttpParams;
  payload?: unknown;
  queryParams?: CustomHttpParams;
  reportProgress?: boolean;
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  withCredentials?: boolean;
}

interface RequestMetadata {
  uri: string;
  headers: HttpHeaders;
  params: HttpParams;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly httpClient = inject(HttpClient);

  public get<T = void>(options: HttpConfigOptions): Observable<T> {
    return this.makeRequest<T>('GET', options);
  }

  public post<T = void>(options: HttpConfigOptions): Observable<T> {
    return this.makeRequest<T>('POST', options);
  }

  public put<T = void>(options: HttpConfigOptions): Observable<T> {
    return this.makeRequest<T>('PUT', options);
  }

  public patch<T = void>(options: HttpConfigOptions): Observable<T> {
    return this.makeRequest<T>('PATCH', options);
  }

  public delete<T = void>(options: HttpConfigOptions): Observable<T> {
    return this.makeRequest<T>('DELETE', options);
  }

  private makeRequest<T>(method: string, options: HttpConfigOptions): Observable<T> {
    const { observe, responseType, payload, withCredentials } = options;
    const { uri, headers, params } = this.getRequestMetadata(options);
    return this.httpClient.request(method, uri, {
      ...(['GET', 'HEAD'].includes(method) || !payload ? {} : { body: payload }),
      headers,
      params,
      observe,
      responseType,
      withCredentials,
    }) as Observable<T>;
  }

  private buildUri({ endpoint = '', params }: HttpConfigOptions): string {
    if (!params || !Object.keys(params).length) return endpoint;
    return this.replaceEndpointWithParams(endpoint, params);
  }

  private replaceEndpointWithParams(endpoint: string, params: HttpParams | CustomHttpParams): string {
    return Object.keys(params).reduce(
      (acc, key) => acc.replace(`{${key}}`, String((params as CustomHttpParams)[key])),
      endpoint,
    );
  }

  private getRequestMetadata(options: HttpConfigOptions): RequestMetadata {
    const uri = this.buildUri(options);
    const headers = this.buildHeaders(options);
    const params = new HttpParams({ fromObject: options.queryParams ?? {} });
    return { uri, headers, params };
  }

  private buildHeaders({ headers }: HttpConfigOptions): HttpHeaders {
    const extra =
      headers instanceof HttpHeaders
        ? headers.keys().reduce<Record<string, string>>((acc, key) => {
            const value = headers.get(key);
            return value === null ? acc : { ...acc, [key]: value };
          }, {})
        : (headers ?? {});
    return new HttpHeaders({ Accept: 'application/json', 'Content-Type': 'application/json', ...extra });
  }
}
